import React, { useEffect, useState } from 'react'
import { bool, func, object } from 'prop-types'
import { useIntl } from 'react-intl'
import styled, { useTheme } from 'styled-components'
import { rem } from 'polished'

import { authenticationMessages, userMenuMessages } from '../../../translations'
import { baseTiltifyUrl } from '../../../utils/tiltifyUrl'
import { Button } from '../../Button'
import { Form, useFormContext } from '../../Form'
import { EmailInput, PasswordInput } from '../Inputs'
import { Social } from '../Social'
import { SubmitButton } from '../SubmitButton'
import { CauseSection } from './CauseSection'
import { CaretRight } from '../../../icons'

const StyledHeading = styled.div`
  font-size: ${rem(24)};
  font-weight: bold;
  text-align: center;
  margin-bottom: ${rem(24)};
  color: ${({ theme }) => theme.textColor};
`

const StyledLoginFooter = styled.div`
  margin: ${rem(26)} -${rem(29)} 0;
  padding: ${rem(18)} ${rem(16)} 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  display: flex;
  justify-content: space-between;

  @media ${({ theme }) => `(max-width: ${theme.breakpoints[0]})`} {
    flex-direction: column;
    text-align: center;
  }
`

const ErrorDiv = styled.div`
  background-color: ${({ theme }) => theme.danger};
  padding: 0.5rem;
  text-align: center;
`

const StyledButton = styled(Button)`
  text-decoration: underline;
  color: ${({ theme }) => theme.lightBlue};
  font-weight: bold;
  font-size: ${rem(14)};
`

const PasswordButtonWrapper = styled.div`
  margin-top: ${rem(22)};
  display: flex;
  justify-content: center;
`

const StyledPasswordButton = styled(Button)`
  text-decoration: underline;
  color: ${({ theme }) => theme.lightBlue};
  font-size: ${rem(16)};
  line-height: ${rem(12)};
`

const SubButton = ({ externalErrors, passwordless }) => {
  const { formatMessage } = useIntl()
  const { isSubmitting, setSubmitting, errors } = useFormContext()
  const theme = useTheme()

  useEffect(() => {
    if (errors || externalErrors) {
      setSubmitting(false)
    }
  }, [externalErrors, errors])
  return (
    <SubmitButton
      loading={isSubmitting}
      iconRight={
        passwordless ? (
          <CaretRight
            height={19}
            width={19}
            stroke={theme.isDark ? theme.darkGrey : theme.darkTextColor}
            fill={theme.isDark ? theme.darkGrey : theme.darkTextColor}
          />
        ) : undefined
      }
    >
      {passwordless ? 'Send me a link' : formatMessage(userMenuMessages.signInText)}
    </SubmitButton>
  )
}

SubButton.propTypes = {
  externalErrors: object,
  passwordless: bool,
}

export const LoginFlow = ({
  handleRedirect,
  onPasswordlessLogin,
  onFlowChange,
  onLogin,
  openChild,
  errors,
}) => {
  const { formatMessage } = useIntl()
  const [showPasswordLogin, setShowPasswordLogin] = useState(false)

  useEffect(() => {}, [errors])

  return (
    <div id='gtm_login'>
      <StyledHeading>{formatMessage(authenticationMessages.signInToContinue)}</StyledHeading>
      {showPasswordLogin ? (
        <Form
          initialValues={{
            email: '',
            password: '',
          }}
          onSubmit={onLogin}
        >
          {errors && errors.message ? <ErrorDiv>{errors.message}</ErrorDiv> : null}
          <EmailInput />
          <PasswordInput />
          <SubButton externalErrors={errors} />
          <PasswordButtonWrapper>
            <StyledPasswordButton onClick={() => setShowPasswordLogin(false)}>
              {'Log in with a link'}
            </StyledPasswordButton>
          </PasswordButtonWrapper>
        </Form>
      ) : (
        <Form
          initialValues={{
            email: '',
          }}
          onSubmit={onPasswordlessLogin}
        >
          {errors && errors.message ? <ErrorDiv>{errors.message}</ErrorDiv> : null}
          <EmailInput />
          <SubButton externalErrors={errors} passwordless />
          <PasswordButtonWrapper>
            <StyledPasswordButton onClick={() => setShowPasswordLogin(true)}>
              {'Log in with a password'}
            </StyledPasswordButton>
          </PasswordButtonWrapper>
        </Form>
      )}
      <Social token={handleRedirect} openChild={openChild} />
      <CauseSection />

      <StyledLoginFooter>
        <StyledButton forwardedAs='a' href={`${baseTiltifyUrl()}/users/password/new`}>
          {formatMessage(authenticationMessages.forgotPassword)}
        </StyledButton>
        <StyledButton onClick={onFlowChange}>
          {formatMessage(authenticationMessages.createAnAccount)}
        </StyledButton>
      </StyledLoginFooter>
    </div>
  )
}

LoginFlow.propTypes = {
  onFlowChange: func,
  onLogin: func,
  onPasswordlessLogin: func,
  errors: object,
  handleRedirect: func,
  openChild: func,
}
